// Dispatch tactical map — orchestrates the layer components split into
// components/dispatchmap/ (refactor: behavior unchanged, plus additive ZONES layer).
import React, { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker, Tooltip } from "react-leaflet";
import client from "@/lib/api";
import { toast } from "sonner";
import { DISC } from "@/components/badges";
import { CENTER, BASEMAPS, valid, incidentIcon } from "@/components/dispatchmap/mapShared";
import { UnitLayer } from "@/components/dispatchmap/UnitLayer";
import { CenterTracker, SelfLayer, MapRefCapture, AutoResize, FollowMe, EtaToCall, Fit, RouteLayer, CoverageLayer, HotspotMarkers, ZoneLayer } from "@/components/dispatchmap/TacticalLayers";
import { FacilityLayer, CameraLayer, LiveCameraLayer, PaCameraLayer, BaltHeatLayer, BpdDistrictLayer, PaPoliceLayer, FocusJurisdiction, PspStationLayer, BALT_CATS, BALT_ICON, ALL_BALT_CATS, loadBaltCats } from "@/components/dispatchmap/OverlayLayers";

// Unit status filter (dispatch decision support — additive control).
const ST_TABS = [["all", "ALL"], ["available", "AVAIL"], ["enroute", "ENRT"],
  ["onscene", "SCENE"], ["emergency", "EMERG"], ["offline", "OFFLINE"]];
function stMatch(u, f) {
  if (f === "all") return true;
  if (f === "available") return u.status === "available" || u.status === "clear";
  if (f === "enroute") return ["dispatched", "enroute", "transport"].includes(u.status);
  if (f === "onscene") return u.status === "onscene";
  if (f === "emergency") return u.status === "emergency";
  if (f === "offline") return u.gps?.online === false || !u.gps?.ts || (Date.now() - new Date(u.gps.ts).getTime()) > 900000;
  return true;
}

const DISC_TABS = [["all", "ALL"], ["police", "PD"], ["fire", "FIRE"], ["ems", "EMS"]];

export default function DispatchMap({ units = [], incidents = [], height = "100%", onSelectIncident, showDisciplineFilter = true, showRoutes = false, selectedIncidentId = null, enableCoverage = false, hotspots = [], selfPos = null, onRefreshFix = null, etaTarget = null }) {
  const [eta, setEta] = useState(null);
  const [disc, setDisc] = useState("all");
  const [stFilter, setStFilter] = useState("all");
  const [trail, setTrail] = useState(null);
  const mapRef = useRef(null);
  const loadTrail = async (u) => {
    try {
      const { data } = await client.get(`/gps/history/${encodeURIComponent(u.unit_id)}`, { params: { hours: 8 } });
      if (!data.points?.length) { toast(`No breadcrumb history for ${u.unit_id} yet`); return; }
      setTrail({ unit: u.unit_id, points: data.points });
    } catch (e) {
      toast.error(e.response?.status === 403 ? "Not authorized for location history" : "Trail unavailable");
    }
  };
  const [callerTrails, setCallerTrails] = useState({});
  const trailKey = incidents.filter((i) => i.caller_location).map((i) => `${i.cc_number}:${i.caller_location.ts}`).join("|");
  useEffect(() => {
    if (!trailKey) { setCallerTrails({}); return; }
    client.get("/e911/trails").then((r) => setCallerTrails(r.data.trails || {})).catch(() => {});
  }, [trailKey]);
  const [covOn, setCovOn] = useState(false);
  const [covMin, setCovMin] = useState(8);
  const [facOn, setFacOn] = useState(false);
  const [camOn, setCamOn] = useState(() => localStorage.getItem("map_cams") === "1");
  const toggleCams = () => setCamOn((v) => { localStorage.setItem("map_cams", v ? "0" : "1"); return !v; });
  const [liveCamOn, setLiveCamOn] = useState(() => localStorage.getItem("map_livecams") === "1");
  const toggleLiveCams = () => setLiveCamOn((v) => { localStorage.setItem("map_livecams", v ? "0" : "1"); return !v; });
  const [paCamOn, setPaCamOn] = useState(() => localStorage.getItem("map_pacams") === "1");
  const togglePaCams = () => setPaCamOn((v) => { localStorage.setItem("map_pacams", v ? "0" : "1"); return !v; });
  const [baltOn, setBaltOn] = useState(() => localStorage.getItem("map_balt") === "1");
  const toggleBalt = () => setBaltOn((v) => { localStorage.setItem("map_balt", v ? "0" : "1"); return !v; });
  const [zonesOn, setZonesOn] = useState(() => localStorage.getItem("map_zones") !== "0"); // default ON
  const [bpdOn, setBpdOn] = useState(() => localStorage.getItem("map_bpd") === "1");
  const toggleBpd = () => setBpdOn((v) => { localStorage.setItem("map_bpd", v ? "0" : "1"); return !v; });
  const [paOn, setPaOn] = useState(() => localStorage.getItem("map_pa_police") === "1");
  const togglePa = () => setPaOn((v) => { localStorage.setItem("map_pa_police", v ? "0" : "1"); return !v; });
  const [pspOn, setPspOn] = useState(() => localStorage.getItem("map_psp") === "1");
  const togglePsp = () => setPspOn((v) => { localStorage.setItem("map_psp", v ? "0" : "1"); return !v; });
  const toggleZones = () => setZonesOn((v) => { localStorage.setItem("map_zones", v ? "0" : "1"); return !v; });
  const [baltCats, setBaltCats] = useState(loadBaltCats);
  const toggleBaltCat = (k) => setBaltCats((prev) => {
    const next = prev.includes(k) ? prev.filter((c) => c !== k) : [...prev, k];
    localStorage.setItem("map_balt_cats", JSON.stringify(next));
    return next;
  });
  const resetBaltCats = () => { localStorage.setItem("map_balt_cats", JSON.stringify(ALL_BALT_CATS)); setBaltCats(ALL_BALT_CATS); };
  const [basemap, setBasemap] = useState(() => localStorage.getItem("map_basemap") || "dark");
  const [wazeUrl, setWazeUrl] = useState(null);
  const viewRef = useRef({ c: { lat: CENTER[0], lng: CENTER[1] }, z: 13 });
  const [followMe, setFollowMe] = useState(() => localStorage.getItem("mdt_follow_me") === "1");
  const [headingUp, setHeadingUp] = useState(() => localStorage.getItem("mdt_heading_up") === "1");
  const toggleHeadingUp = () => setHeadingUp((v) => { localStorage.setItem("mdt_heading_up", v ? "0" : "1"); return !v; });
  const rotateDeg = followMe && headingUp && Number.isFinite(selfPos?.heading) ? -selfPos.heading : 0;
  const mph = Number.isFinite(selfPos?.speed) ? Math.round(selfPos.speed * 2.23694) : null;
  const toggleFollow = () => setFollowMe((v) => { localStorage.setItem("mdt_follow_me", v ? "0" : "1"); if (!v && selfPos) mapRef.current?.setView([selfPos.lat, selfPos.lng], Math.max(mapRef.current.getZoom(), 15)); return !v; });
  const pickBasemap = (k) => { setBasemap(k); localStorage.setItem("map_basemap", k); };
  const toggleWaze = () => {
    if (wazeUrl) { setWazeUrl(null); return; }
    const { c, z } = viewRef.current;
    setWazeUrl(`https://embed.waze.com/iframe?zoom=${Math.min(Math.max(z, 10), 17)}&lat=${c.lat.toFixed(5)}&lon=${c.lng.toFixed(5)}&ct=livemap`);
  };
  const fUnits = disc === "all" ? units : units.filter((u) => u.type === disc);
  const fIncs = disc === "all" ? incidents : incidents.filter((i) => i.discipline === disc);
  const vUnits = fUnits.filter((u) => valid(u.lat, u.lng) && stMatch(u, stFilter));
  const incPts = fIncs.filter((i) => valid(i.location?.lat, i.location?.lng));
  const pts = [...vUnits.map((u) => [u.lat, u.lng]), ...incPts.map((i) => [i.location.lat, i.location.lng])];

  return (
    <div style={{ height, position: "relative" }} data-testid="dispatch-map">
      {showDisciplineFilter && (
        <div data-testid="map-disc-filter" className="absolute top-2 right-2 z-[500] flex gap-1 rounded-md p-1" style={{ background: "rgba(10,10,10,0.82)", border: "1px solid #2C2D35" }}>
          {DISC_TABS.map(([d, lab]) => (
            <button key={d} data-testid={`map-disc-${d}`} onClick={() => setDisc(d)}
              className="px-2 py-1 text-[10px] font-head rounded transition-colors"
              style={disc === d ? { background: (DISC[d] || {}).color || "#3B82F6", color: "#fff" } : { background: "transparent", color: "#9CA3AF" }}>
              {lab}
            </button>
          ))}
        </div>
      )}
      {showDisciplineFilter && (
        <div data-testid="map-status-filter" className="absolute top-11 right-2 z-[500] flex gap-1 rounded-md p-1" style={{ background: "rgba(10,10,10,0.82)", border: "1px solid #2C2D35" }}>
          {ST_TABS.map(([k, lab]) => (
            <button key={k} data-testid={`map-status-${k}`} onClick={() => setStFilter(k)}
              className="px-1.5 py-0.5 text-[9px] font-head rounded transition-colors"
              style={stFilter === k ? { background: "#22D3EE", color: "#04202b" } : { background: "transparent", color: "#9CA3AF" }}>
              {lab}
            </button>
          ))}
        </div>
      )}
      {(selfPos || trail) && (
        <div data-testid="map-gps-controls" className="absolute bottom-16 right-2 z-[500] flex flex-col gap-1">
          {selfPos && (
            <button data-testid="map-center-on-me" onClick={() => mapRef.current?.setView([selfPos.lat, selfPos.lng], 16)}
              className="px-2.5 py-1.5 text-[10px] font-head font-bold rounded-md"
              style={{ background: "#22D3EE", color: "#04202b", border: "1px solid #22D3EE" }}>
              ⌖ CENTER ON ME
            </button>
          )}
          {selfPos && (
            <button data-testid="map-follow-me" onClick={toggleFollow} aria-pressed={followMe}
              className="px-2.5 py-1.5 text-[10px] font-head font-bold rounded-md"
              style={followMe
                ? { background: "#22C55E", color: "#04200f", border: "1px solid #22C55E", boxShadow: "0 0 0 3px rgba(34,197,94,.3)" }
                : { background: "rgba(10,10,10,0.86)", color: "#22C55E", border: "1px solid #22C55E" }}>
              {followMe ? "● FOLLOWING ME" : "○ FOLLOW ME"}
            </button>
          )}
          {selfPos && followMe && (
            <button data-testid="map-heading-up" onClick={toggleHeadingUp} aria-pressed={headingUp}
              className="px-2.5 py-1.5 text-[10px] font-head font-bold rounded-md"
              style={headingUp
                ? { background: "#F59E0B", color: "#2a1800", border: "1px solid #F59E0B" }
                : { background: "rgba(10,10,10,0.86)", color: "#F59E0B", border: "1px solid #F59E0B" }}>
              {headingUp ? "▲ HEADING UP" : "N NORTH UP"}
            </button>
          )}
          {selfPos && onRefreshFix && (
            <button data-testid="map-refresh-location" onClick={onRefreshFix}
              className="px-2.5 py-1.5 text-[10px] font-head font-bold rounded-md"
              style={{ background: "rgba(10,10,10,0.86)", color: "#22D3EE", border: "1px solid #22D3EE" }}>
              ↻ REFRESH LOCATION
            </button>
          )}
          {trail && (
            <button data-testid="map-trail-close" onClick={() => setTrail(null)}
              className="px-2.5 py-1.5 text-[10px] font-head font-bold rounded-md"
              style={{ background: "rgba(10,10,10,0.86)", color: "#F59E0B", border: "1px solid #F59E0B" }}>
              ✕ TRAIL {trail.unit} ({trail.points.length} pts)
            </button>
          )}
        </div>
      )}
      {enableCoverage && (
        <div data-testid="coverage-control" className="absolute z-[500] flex flex-col gap-1 rounded-md p-1.5" style={{ top: showDisciplineFilter ? "42px" : "8px", right: "8px", background: "rgba(10,10,10,0.86)", border: "1px solid #2C2D35", minWidth: "132px" }}>
          <button data-testid="coverage-toggle" onClick={() => setCovOn((v) => !v)}
            className="px-2 py-1 text-[10px] font-head rounded transition-colors flex items-center justify-between"
            style={covOn ? { background: "#10B981", color: "#04120b" } : { background: "transparent", color: "#9CA3AF", border: "1px solid #2C2D35" }}>
            COVERAGE <span>{covOn ? "ON" : "OFF"}</span>
          </button>
          <button data-testid="facilities-toggle" onClick={() => setFacOn((v) => !v)}
            className="px-2 py-1 text-[10px] font-head rounded transition-colors flex items-center justify-between"
            style={facOn ? { background: "#22D3EE", color: "#04202b" } : { background: "transparent", color: "#9CA3AF", border: "1px solid #2C2D35" }}>
            FACILITIES <span>{facOn ? "ON" : "OFF"}</span>
          </button>
          {covOn && (
            <>
              <div className="flex gap-1">
                {[5, 8, 12].map((m) => (
                  <button key={m} data-testid={`coverage-min-${m}`} onClick={() => setCovMin(m)}
                    className="flex-1 px-1 py-0.5 text-[10px] font-head rounded transition-colors"
                    style={covMin === m ? { background: "#3B82F6", color: "#fff" } : { background: "transparent", color: "#9CA3AF", border: "1px solid #2C2D35" }}>
                    {m}m
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1 mt-0.5">
                <span className="text-[8px] font-mono text-[#9CA3AF]">Covered</span>
                <span className="flex-1 h-1.5 rounded" style={{ background: "linear-gradient(90deg, hsl(130,82%,45%), hsl(65,82%,45%), hsl(0,82%,45%))" }} />
                <span className="text-[8px] font-mono text-[#9CA3AF]">Gap</span>
              </div>
              {hotspots.length > 0 && (
                <div data-testid="coverage-hotspot-note" className="text-[8px] font-mono" style={{ color: "#F97316" }}>
                  ◉ DEMAND-WEIGHTED · {hotspots.length} HOT-SPOTS (90d)
                </div>
              )}
            </>
          )}
        </div>
      )}
      {baltOn && (
        <div data-testid="map-balt-cat-filter" className="absolute bottom-16 left-2 z-[500] flex flex-wrap gap-1 rounded-md p-1 items-center" style={{ background: "rgba(10,10,10,0.82)", border: "1px solid #F97316", maxWidth: 420 }}>
          <span className="text-[8px] font-mono font-bold px-1" style={{ color: "#F97316" }}>CFS ICONS:</span>
          {BALT_CATS.map(([k, lab]) => {
            const on = baltCats.includes(k);
            return (
              <button key={k} data-testid={`map-balt-cat-${k}`} onClick={() => toggleBaltCat(k)}
                className="px-1.5 py-0.5 text-[9px] font-head font-bold rounded-full transition-colors"
                style={on ? { background: "#F97316", color: "#2b1204" } : { background: "transparent", color: "#6B7280", border: "1px solid #2C2D35" }}>
                {BALT_ICON[k]} {lab}
              </button>
            );
          })}
          <button data-testid="map-balt-cat-all" onClick={resetBaltCats}
            className="px-1.5 py-0.5 text-[9px] font-head font-bold rounded-full transition-colors"
            style={{ background: "transparent", color: "#9CA3AF", border: "1px solid #2C2D35" }}>
            ALL
          </button>
        </div>
      )}
      {/* BASEMAP STYLE + WAZE LIVE TRAFFIC — bottom-left, clear of zoom & filters */}
      <div data-testid="map-style-control" className="absolute bottom-6 left-2 z-[500] flex gap-1 rounded-md p-1" style={{ background: "rgba(10,10,10,0.82)", border: "1px solid #2C2D35" }}>
        {Object.entries(BASEMAPS).map(([k, b]) => (
          <button key={k} data-testid={`map-style-${k}`} onClick={() => pickBasemap(k)}
            className="px-2 py-1 text-[10px] font-head rounded transition-colors"
            style={basemap === k ? { background: "#3B82F6", color: "#fff" } : { background: "transparent", color: "#9CA3AF" }}>
            {b.label}
          </button>
        ))}
        <button data-testid="map-cams-toggle" onClick={toggleCams}
          className="px-2 py-1 text-[10px] font-head font-bold rounded transition-colors"
          style={camOn ? { background: "#22C55E", color: "#04120b" } : { background: "transparent", color: "#22C55E", border: "1px solid #22C55E" }}>
          CAMERAS
        </button>
        <button data-testid="map-livecams-toggle" onClick={toggleLiveCams}
          className="px-2 py-1 text-[10px] font-head font-bold rounded transition-colors"
          style={liveCamOn ? { background: "#A855F7", color: "#1c0a2b" } : { background: "transparent", color: "#A855F7", border: "1px solid #A855F7" }}>
          LIVE MD
        </button>
        <button data-testid="map-pacams-toggle" onClick={togglePaCams}
          className="px-2 py-1 text-[10px] font-head font-bold rounded transition-colors"
          style={paCamOn ? { background: "#FBBF24", color: "#2b1c04" } : { background: "transparent", color: "#FBBF24", border: "1px solid #FBBF24" }}>
          LIVE PA
        </button>
        <button data-testid="map-balt-toggle" onClick={toggleBalt}
          className="px-2 py-1 text-[10px] font-head font-bold rounded transition-colors"
          style={baltOn ? { background: "#F97316", color: "#2b1204" } : { background: "transparent", color: "#F97316", border: "1px solid #F97316" }}>
          BALT CFS
        </button>
        <button data-testid="map-zones-toggle" onClick={toggleZones}
          className="px-2 py-1 text-[10px] font-head font-bold rounded transition-colors"
          style={zonesOn ? { background: "#F59E0B", color: "#2b1c04" } : { background: "transparent", color: "#F59E0B", border: "1px solid #F59E0B" }}>
          ZONES
        </button>
        <button data-testid="map-bpd-toggle" onClick={toggleBpd}
          className="px-2 py-1 text-[10px] font-head font-bold rounded transition-colors"
          style={bpdOn ? { background: "#3B82F6", color: "#061a33" } : { background: "transparent", color: "#3B82F6", border: "1px solid #3B82F6" }}>
          BPD DISTRICTS
        </button>
        <button data-testid="map-pa-police-toggle" onClick={togglePa}
          className="px-2 py-1 text-[10px] font-head font-bold rounded transition-colors"
          style={paOn ? { background: "#10B981", color: "#052e1f" } : { background: "transparent", color: "#10B981", border: "1px solid #10B981" }}>
          PA POLICE JURIS
        </button>
        <button data-testid="map-psp-toggle" onClick={togglePsp}
          className="px-2 py-1 text-[10px] font-head font-bold rounded transition-colors"
          style={pspOn ? { background: "#9CA3AF", color: "#111" } : { background: "transparent", color: "#D1D5DB", border: "1px solid #9CA3AF" }}>
          PSP BARRACKS
        </button>
        <button data-testid="map-waze-toggle" onClick={toggleWaze}
          className="px-2 py-1 text-[10px] font-head font-bold rounded transition-colors"
          style={wazeUrl ? { background: "#33CCFF", color: "#062733" } : { background: "transparent", color: "#33CCFF", border: "1px solid #33CCFF" }}>
          WAZE TRAFFIC
        </button>
      </div>
      {wazeUrl && (
        <div data-testid="map-waze-overlay" className="absolute inset-0 z-[600]" style={{ background: "#0A0A0A" }}>
          <iframe title="Waze Live Traffic" src={wazeUrl} width="100%" height="100%" style={{ border: 0 }} allowFullScreen />
          <button data-testid="map-waze-close" onClick={() => setWazeUrl(null)}
            className="absolute top-2 right-2 z-[610] px-3 py-2 text-xs font-head font-bold rounded text-white"
            style={{ background: "#DC2626" }}>
            ✕ CLOSE TRAFFIC — BACK TO TACTICAL MAP
          </button>
          <div className="absolute bottom-2 left-2 z-[610] px-2 py-1 rounded font-mono text-[9px]" style={{ background: "rgba(10,10,10,0.8)", color: "#33CCFF" }}>
            LIVE WAZE TRAFFIC · crashes · hazards · jams (community-reported)
          </div>
        </div>
      )}
      <div style={{ position: "absolute", inset: rotateDeg ? "-45%" : 0, transform: rotateDeg ? `rotate(${rotateDeg}deg)` : "none", transition: "transform .5s ease", transformOrigin: "center" }}>
      <MapContainer center={CENTER} zoom={13} style={{ height: "100%", width: "100%" }} zoomControl={true}>
        <TileLayer key={basemap} url={BASEMAPS[basemap].url} attribution={BASEMAPS[basemap].attr} />
        <AutoResize />
        <CenterTracker viewRef={viewRef} />
        {enableCoverage && covOn && <CoverageLayer units={vUnits} threshold={covMin} hotspots={hotspots} />}
        {enableCoverage && covOn && <HotspotMarkers hotspots={hotspots} />}
        {facOn && <FacilityLayer />}
        {camOn && <CameraLayer />}
        {liveCamOn && <LiveCameraLayer />}
        {paCamOn && <PaCameraLayer />}
        {baltOn && <BaltHeatLayer cats={baltCats} />}
        {zonesOn && <ZoneLayer />}
        {bpdOn && <BpdDistrictLayer />}
        {paOn && <PaPoliceLayer />}
        {pspOn && <PspStationLayer />}
        <FocusJurisdiction />
        <Fit points={pts} />
        <MapRefCapture mapRef={mapRef} />
        <SelfLayer selfPos={selfPos} headingUp={rotateDeg !== 0} />
        {etaTarget && <EtaToCall selfPos={selfPos} target={etaTarget} onEta={setEta} />}
        <FollowMe selfPos={selfPos} on={followMe} setOn={(v) => { setFollowMe(v); localStorage.setItem("mdt_follow_me", v ? "1" : "0"); }} />
        {trail && (
          <>
            <Polyline positions={trail.points.map((p) => [p.lat, p.lng])} pathOptions={{ color: "#22D3EE", weight: 3, dashArray: "6 6", opacity: 0.85 }} />
            {trail.points.map((p, i) => (
              <CircleMarker key={i} center={[p.lat, p.lng]} radius={4}
                pathOptions={{ color: "#22D3EE", fillColor: "#0d0e11", fillOpacity: 1 }}>
                <Tooltip>
                  <span style={{ fontFamily: "monospace", fontSize: 10 }}>
                    {new Date(p.ts).toLocaleTimeString("en-GB", { hour12: false }).slice(0, 5)} — {p.status || "?"}{p.incident ? ` · ${p.incident}` : ""}
                  </span>
                </Tooltip>
              </CircleMarker>
            ))}
          </>
        )}
        {showRoutes && <RouteLayer units={vUnits} incidents={incPts} selectedId={selectedIncidentId} />}
        <UnitLayer units={vUnits} onTrail={loadTrail} />
        {incPts.map((i) => {
          const c = i.priority === 1 ? "#DC2626" : (DISC[i.discipline] || {}).color || "#9CA3AF";
          return (
            <Marker key={i.id} position={[i.location.lat, i.location.lng]} icon={incidentIcon(c, i.priority === 1)}
              eventHandlers={{ click: () => onSelectIncident && onSelectIncident(i) }}>
              <Popup>
                <div className="font-mono text-xs">
                  <b>{i.public_ref ? `CAD# ${i.public_ref}` : i.cc_number}</b>{i.public_ref ? <span style={{ color: "#9CA3AF" }}> · Case {i.cc_number}</span> : null}<br />
                  {i.call_type_label}<br />
                  {i.location.address}<br />
                  P{i.priority} · {i.status}
                  {i.caller_location && <><br /><span style={{ color: "#10B981" }}>📍 Caller GPS ±{Math.round(i.caller_location.accuracy_m || 0)} m</span></>}
                </div>
              </Popup>
            </Marker>
          );
        })}
        {fIncs.filter((i) => callerTrails[i.cc_number]).map((i) => {
          const pts = callerTrails[i.cc_number].filter((p) => valid(p.lat, p.lng));
          return (
            <React.Fragment key={`ct-${i.id}`}>
              <Polyline positions={pts.map((p) => [p.lat, p.lng])} pathOptions={{ color: "#10B981", weight: 3, opacity: 0.8, dashArray: "6 4" }} />
              {pts.slice(0, -1).map((p, k) => (
                <CircleMarker key={k} center={[p.lat, p.lng]} radius={3} pathOptions={{ color: "#10B981", fillColor: "#064E3B", fillOpacity: 1, weight: 1 }}>
                  <Tooltip direction="top"><span className="font-mono text-[10px]">Caller @ {new Date(p.ts).toLocaleTimeString("en-GB", { hour12: false }).slice(0, 8)}{p.accuracy_m ? ` · ±${Math.round(p.accuracy_m)} m` : ""}</span></Tooltip>
                </CircleMarker>
              ))}
            </React.Fragment>
          );
        })}
        {fIncs.filter((i) => i.caller_location && valid(i.caller_location.lat, i.caller_location.lng)).map((i) => (
          <CircleMarker key={`cl-${i.id}`} center={[i.caller_location.lat, i.caller_location.lng]} radius={Math.max(6, Math.min(18, (i.caller_location.accuracy_m || 30) / 6))}
            pathOptions={{ color: "#10B981", fillColor: "#10B981", fillOpacity: 0.25, weight: 2, dashArray: "3 3" }} eventHandlers={{ click: () => onSelectIncident && onSelectIncident(i) }}>
            <Tooltip direction="top" permanent={false}><span className="font-mono text-[10px]">📍 Caller GPS · {i.public_ref ? `CAD# ${i.public_ref}` : i.cc_number}{i.caller_location.accuracy_m ? ` · ±${Math.round(i.caller_location.accuracy_m)} m` : ""}</span></Tooltip>
          </CircleMarker>
        ))}
      </MapContainer>
      </div>
      {selfPos && ((followMe && (Number.isFinite(selfPos.heading) || mph != null)) || (etaTarget && eta)) && (
        <div data-testid="map-nav-hud" className="absolute top-2 left-14 z-[500] px-3 py-1.5 rounded-md font-mono flex items-center gap-3"
          style={{ background: "rgba(10,10,10,0.86)", border: "1px solid #22D3EE", color: "#22D3EE" }}>
          {followMe && Number.isFinite(selfPos.heading) && (
            <span className="flex items-center gap-1 text-[11px] font-bold">
              <span style={{ display: "inline-block", transform: `rotate(${rotateDeg ? 0 : selfPos.heading}deg)`, transition: "transform .4s" }}>▲</span>
              {Math.round(selfPos.heading).toString().padStart(3, "0")}° {["N","NE","E","SE","S","SW","W","NW"][Math.round(selfPos.heading / 45) % 8]}
            </span>
          )}
          {followMe && mph != null && <span className="text-[13px] font-bold">{mph}<span className="text-[9px] ml-0.5 opacity-70">MPH</span></span>}
          {etaTarget && eta && (
            <span data-testid="map-eta-to-call" className="flex items-center gap-2 pl-3 text-[11px] font-bold" style={{ borderLeft: followMe ? "1px solid #2C2D35" : "none", color: "#F59E0B" }}>
              <span className="text-[9px] opacity-70">→ {eta.cc}</span>
              <span>{(eta.distance_m / 1609.34).toFixed(1)} mi</span>
              <span>ETA {eta.duration_s < 60 ? `${eta.duration_s}s` : `${Math.round(eta.duration_s / 60)} min`}{eta.fallback ? " ~" : ""}</span>
              <span className="text-[9px] opacity-70">{new Date(Date.now() + eta.duration_s * 1000).toLocaleTimeString("en-GB", { hour12: false }).slice(0, 5)}</span>
            </span>
          )}
          {rotateDeg !== 0 && <span className="text-[9px]" style={{ color: "#F59E0B" }}>HEADING UP</span>}
        </div>
      )}
    </div>
  );
}
